/**
 * Drawing helpers for the UI icons and reward pictures (booster squares, retention buttons,
 * the ad clapper, the Day-7 chest, the jigsaw). They sit on top of toon-kit: the same ink and
 * the same light from the upper left, tuned for pictures that are shown at 34–46 px.
 *
 * At that size the creature-scale line (LINE_BODY × r) disappears, so icons ink at a share of
 * the whole frame instead (ICON_LINE ≈ 5 % of the frame, about 2 px on screen), and a whole
 * icon can sit on a hard ink drop like Ghost Circuit's HUD icons (`drop-shadow(0 1.5px 0 ink)`).
 */
import { INK, LINE_BODY } from "../palette.js";
import { TAU, inkStyle, sparklePath } from "../toon-kit.js";
import { drawCreature } from "../creatures/index.js";

/** Silhouette ink for a 256 px icon frame (about 2 px once shown at 40 px). */
export var ICON_LINE = 12.5;
/** Inner detail ink for a 256 px icon frame. */
export var ICON_DETAIL = 7;

/** Adds a rounded rectangle to the current path (no roundRect dependency for old WebViews). */
export function roundRectPath(c, x, y, w, h, r) {
  var rr = Math.max(0, Math.min(r, w / 2, h / 2));
  c.moveTo(x + rr, y);
  c.lineTo(x + w - rr, y);
  c.arcTo(x + w, y, x + w, y + rr, rr);
  c.lineTo(x + w, y + h - rr);
  c.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  c.lineTo(x + rr, y + h);
  c.arcTo(x, y + h, x, y + h - rr, rr);
  c.lineTo(x, y + rr);
  c.arcTo(x, y, x + rr, y, rr);
  c.closePath();
}

/**
 * Adds a rounded five-point star centred on (x, y) to the current path: points at radius R,
 * valleys at Ri, every corner softened with a quadratic curve.
 */
export function roundStarPath(c, x, y, R, Ri, rot) {
  var pts = [];
  for (var i = 0; i < 10; i++) {
    var a = (rot || 0) - Math.PI / 2 + (i / 10) * TAU;
    var d = i % 2 ? Ri : R;
    pts.push([x + Math.cos(a) * d, y + Math.sin(a) * d]);
  }
  var m0 = midpoint(pts[9], pts[0]);
  c.moveTo(m0[0], m0[1]);
  for (var k = 0; k < 10; k++) {
    var p = pts[k], m = midpoint(p, pts[(k + 1) % 10]);
    c.quadraticCurveTo(p[0], p[1], m[0], m[1]);
  }
  c.closePath();
}

function midpoint(a, b) {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/**
 * Hard-stop bands across x (lit left, body, shaded right) for round props, after Ghost
 * Circuit's `celBands`: a gradient object, but with no blend between the tones.
 * @param {{ base: string, light: string, shade: string }} tone
 */
export function celBands(c, x0, x1, tone, opts) {
  var o = opts || {};
  var lit = o.lit != null ? o.lit : 0.18, dark = o.dark != null ? o.dark : 0.66;
  var g = c.createLinearGradient(x0, 0, x1, 0);
  g.addColorStop(0, tone.base);
  g.addColorStop(0.06, tone.light);
  g.addColorStop(lit + 0.06, tone.light);
  g.addColorStop(lit + 0.06, tone.base);
  g.addColorStop(dark, tone.base);
  g.addColorStop(dark, tone.shade);
  g.addColorStop(1, tone.shade);
  return g;
}

/** The same down y: a lit top band, the body, a shaded foot. */
export function celBandsY(c, y0, y1, tone, opts) {
  var o = opts || {};
  var lit = o.lit != null ? o.lit : 0.16, dark = o.dark != null ? o.dark : 0.7;
  var g = c.createLinearGradient(0, y0, 0, y1);
  g.addColorStop(0, tone.light);
  g.addColorStop(lit, tone.light);
  g.addColorStop(lit, tone.base);
  g.addColorStop(dark, tone.base);
  g.addColorStop(dark, tone.shade);
  g.addColorStop(1, tone.shade);
  return g;
}

/**
 * Stroke the current path as an inked rope: a wide ink stroke, then the colour core, so a
 * line reads as a solid inked part (handles, rings, motion arcs, dashed rings).
 */
export function inkedRope(c, width, color, inkWidth, ink) {
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = ink || INK;
  c.lineWidth = width + inkWidth * 2;
  c.stroke();
  c.strokeStyle = color;
  c.lineWidth = width;
  c.stroke();
}

/** A four-point sparkle with an ink edge (for reward glitter that must read on any button). */
export function inkSparkle(c, x, y, r, fill, opts) {
  var o = opts || {};
  c.beginPath();
  sparklePath(c, x, y, r, o.rot || 0);
  c.fillStyle = fill || "#ffffff";
  c.fill();
  inkStyle(c, o.line != null ? o.line : Math.max(1, r * 0.2), o.ink);
  c.stroke();
}

/**
 * A creature bubble for an icon: the game's own creature drawer, with an icon-weight ink ring
 * round the disc so it still reads as a bubble at 40 px.
 */
export function iconCreature(c, assetId, x, y, r, opts) {
  var o = opts || {};
  c.save();
  c.translate(x, y);
  if (o.rot) c.rotate(o.rot);
  drawCreature(c, assetId, r, { t: 0, mood: o.mood });
  c.restore();
  var ring = o.line != null ? o.line : ICON_DETAIL;
  if (ring > r * LINE_BODY) {
    c.beginPath();
    c.arc(x, y, r + ring * 0.5 - r * LINE_BODY * 0.5, 0, TAU);
    inkStyle(c, ring, o.ink);
    c.stroke();
  }
}

/** Device pixels per logical unit of the context's current transform. */
function pixelScale(c) {
  if (typeof c.getTransform !== "function") return 1;
  var m = c.getTransform();
  return Math.max(1, Math.hypot(m.a, m.b));
}

function scratch(w, h) {
  var cv = document.createElement("canvas");
  cv.width = Math.max(1, Math.ceil(w));
  cv.height = Math.max(1, Math.ceil(h));
  return cv;
}

/**
 * A CPU-backed 2D context for a scratch canvas: icons are baked into CPU canvases that get
 * PNG-encoded, and compositing a GPU-backed scratch into one forces a slow GPU readback.
 */
function scratchContext(cv) {
  return cv.getContext("2d", { willReadFrequently: true }) || cv.getContext("2d");
}

/**
 * Paint an icon over a hard ink drop of its own silhouette, `dy` lower: the sticker look that
 * keeps a light icon readable on any button. The icon is painted once into a scratch canvas at
 * the target's pixel scale, its alpha is filled with ink for the drop, then both are copied in.
 * Call at the drawer's top level (frame origin at 0, 0).
 *
 * @param {CanvasRenderingContext2D} c
 * @param {number} w frame width
 * @param {number} h frame height
 * @param {number} dy drop offset (logical units)
 * @param {(c: CanvasRenderingContext2D) => void} paint draws the icon in frame units
 * @param {{ alpha?: number, ink?: string }} [opts]
 */
export function withInkDrop(c, w, h, dy, paint, opts) {
  var o = opts || {};
  if (typeof document === "undefined") {
    paint(c);
    return;
  }
  var s = pixelScale(c);
  var art = scratch(w * s, h * s);
  var ac = scratchContext(art);
  if (!ac) {
    paint(c);
    return;
  }
  ac.scale(s, s);
  paint(ac);
  var drop = scratch(w * s, h * s);
  var dc = scratchContext(drop);
  dc.drawImage(art, 0, 0);
  dc.globalCompositeOperation = "source-in";
  dc.fillStyle = o.ink || INK;
  dc.fillRect(0, 0, drop.width, drop.height);
  c.save();
  c.globalAlpha *= o.alpha != null ? o.alpha : 1;
  c.drawImage(drop, 0, dy, w, h);
  c.restore();
  c.drawImage(art, 0, 0, w, h);
}

export { TAU, INK };
